'use client'

import * as React from 'react'
import { trackToolUse } from '@/lib/analytics/ga4'

// Inline production-cost calculator for /pricing. The visitor's own numbers
// drive it: how many videos they commission per month and what one costs
// them. We compare that annual spend against the published Idukki range
// ($99-$299/mo flat) plus the marginal cost of UGC itself, which is zero:
// the content already exists on social. Defaults are editable placeholders,
// not benchmarks; no invented industry numbers anywhere.

const IDUKKI_MONTHLY_LOW = 99
const IDUKKI_MONTHLY_HIGH = 299

const gbp = (n: number) => '$' + Math.round(n).toLocaleString('en-US')

export function UgcCostCalculator() {
  const [videosPerMonth, setVideosPerMonth] = React.useState(4)
  const [costPerVideo, setCostPerVideo] = React.useState(400)
  const touched = React.useRef(false)

  const producedYear = videosPerMonth * costPerVideo * 12
  const idukkiLow = IDUKKI_MONTHLY_LOW * 12
  const idukkiHigh = IDUKKI_MONTHLY_HIGH * 12
  const savesLow = producedYear - idukkiHigh
  const wins = savesLow > 0

  function onFirstUse() {
    if (touched.current) return
    touched.current = true
    trackToolUse('ugc-cost-calculator', 'adjust')
  }

  return (
    <section className="container-wide pb-16" aria-labelledby="ugc-calc-title">
      <div className="rounded-2xl border bg-card p-8 md:p-10">
        <div className="grid gap-10 md:grid-cols-2 md:items-start">
          <div>
            <h2 id="ugc-calc-title" className="font-display text-3xl font-medium tracking-tight md:text-4xl text-balance">
              What does produced video cost you today?
            </h2>
            <p className="mt-4 max-w-xl text-muted-foreground leading-relaxed text-pretty">
              Put in your own numbers. The comparison is your commissioned-video spend against a
              flat Idukki subscription, where the videos are the ones customers and creators
              already post about you.
            </p>

            <div className="mt-8 space-y-6">
              <label className="block">
                <span className="flex items-baseline justify-between text-sm font-medium">
                  Videos you commission per month
                  <span className="font-display text-lg tabular-nums">{videosPerMonth}</span>
                </span>
                <input
                  type="range"
                  min={1}
                  max={30}
                  value={videosPerMonth}
                  onChange={(e) => { onFirstUse(); setVideosPerMonth(Number(e.target.value)) }}
                  className="mt-2 w-full accent-[hsl(var(--primary))]"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium">Cost per finished video</span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  Editable placeholder, not a benchmark. Use your last quote (script, shoot, edit).
                </span>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">$</span>
                  <input
                    type="number"
                    min={0}
                    step={50}
                    value={costPerVideo}
                    onChange={(e) => { onFirstUse(); setCostPerVideo(Math.max(0, Number(e.target.value))) }}
                    className="h-10 w-32 rounded-lg border bg-background px-3 text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label="Cost per finished video in dollars"
                  />
                </div>
              </label>
            </div>
          </div>

          <div className="rounded-2xl border bg-secondary/30 p-6 md:p-7">
            <dl className="space-y-5">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-sm text-muted-foreground">Commissioned video, per year</dt>
                <dd className="font-display text-2xl font-semibold tabular-nums">{gbp(producedYear)}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 border-t pt-5">
                <dt className="text-sm text-muted-foreground">Idukki flat subscription, per year</dt>
                <dd className="font-display text-2xl font-semibold tabular-nums">
                  {gbp(idukkiLow)}&ndash;{gbp(idukkiHigh)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 border-t pt-5">
                <dt className="text-sm font-medium">
                  {wins ? 'Left on the table each year' : 'Difference each year'}
                </dt>
                <dd className={`font-display text-3xl font-extrabold tabular-nums ${wins ? 'text-primary' : ''}`}>
                  {wins ? gbp(savesLow) + '+' : gbp(Math.abs(savesLow))}
                </dd>
              </div>
            </dl>
            <p className="mt-5 text-xs leading-relaxed text-muted-foreground/80">
              The UGC itself costs nothing to produce: customers already posted it. Idukki
              collects it, clears the rights, tags your products and serves it shoppable. Your
              maths, our price list, nothing else baked in.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
